import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import {
  CLAUDE_EXAM_DOMAINS,
} from "./udyogconnect2.0/data/claudePracticeDomains";
import {
  CLAUDE_PRACTICE_SETTING_FIELDS,
  normalizeClaudePracticeSettings,
} from "./udyogconnect2.0/data/claudePracticeConfig";

const CONTROL_DEFINITIONS = [
  { key: "enabled", label: "Practice test availability", type: "select" },
  { key: "questionCount", label: "Questions per attempt", minimum: 20, maximum: 100 },
  { key: "timeMinutes", label: "Time limit (minutes)", minimum: 30, maximum: 240 },
  { key: "readinessScore", label: "Overall readiness target (%)", minimum: 50, maximum: 100 },
  { key: "domainMinimum", label: "Minimum score in every domain (%)", minimum: 0, maximum: 100 },
];
const EXAM_SIMULATION_SLUG = "claude-architect-foundations";

const ClaudePracticeTestAdmin = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [rows, setRows] = useState([]);
  const [values, setValues] = useState(null);
  const [bankSummary, setBankSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [message, setMessage] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [settingsResponse, bankResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/dynamicvalues`,
          { headers }
        ),
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/exam-simulations/${EXAM_SIMULATION_SLUG}/question-bank-summary`,
          { headers }
        ),
      ]);
      const nextRows = Array.isArray(settingsResponse.data)
        ? settingsResponse.data
        : [];
      setRows(nextRows);
      setValues(normalizeClaudePracticeSettings(nextRows));
      setBankSummary(bankResponse.data);
    } catch (error) {
      setMessage({ severity: "error", text: "Unable to load Claude practice-test settings." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const rowsByField = useMemo(
    () => Object.fromEntries(rows.map((row) => [row.fieldname, row])),
    [rows]
  );

  const updateSetting = async (key) => {
    const fieldname = CLAUDE_PRACTICE_SETTING_FIELDS[key];
    const row = rowsByField[fieldname];
    if (!row) {
      setMessage({ severity: "error", text: `Missing production setting: ${fieldname}` });
      return;
    }

    setSavingKey(key);
    setMessage(null);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/dynamicvalues/${row.id}`,
        { fieldvalue: String(values[key]) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ severity: "success", text: `${CONTROL_DEFINITIONS.find((item) => item.key === key)?.label} updated.` });
      await fetchSettings();
    } catch (error) {
      setMessage({ severity: "error", text: "The setting could not be saved." });
    } finally {
      setSavingKey("");
    }
  };

  if (loading || !values || !bankSummary) return <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box>
          <Typography variant="h5">Claude Practice Test</Typography>
          <Typography color="text.secondary">
            Control availability, attempt structure, timing, and UC readiness thresholds.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate("/2.0/claude-practice-test")}>
          Open practice test
        </Button>
      </Box>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined"><CardContent>
            <Typography color="text.secondary">Question bank</Typography>
            <Typography variant="h4">{bankSummary.questionCount}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined"><CardContent>
            <Typography color="text.secondary">Blueprint domains</Typography>
            <Typography variant="h4">{Object.keys(CLAUDE_EXAM_DOMAINS).length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined"><CardContent>
            <Typography color="text.secondary">Bank version</Typography>
            <Typography variant="h6">{bankSummary.bankVersion}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Exam settings</Typography>
          <Grid container spacing={2}>
            {CONTROL_DEFINITIONS.map((control) => (
              <Grid item xs={12} md={6} key={control.key}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField
                    select={control.type === "select"}
                    type={control.type === "select" ? undefined : "number"}
                    fullWidth
                    size="small"
                    label={control.label}
                    value={values[control.key]}
                    inputProps={
                      control.type === "select"
                        ? undefined
                        : { min: control.minimum, max: control.maximum }
                    }
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [control.key]:
                          control.type === "select"
                            ? event.target.value
                            : Number(event.target.value),
                      }))
                    }
                  >
                    {control.type === "select" && [
                      <MenuItem value={true} key="enabled">Enabled</MenuItem>,
                      <MenuItem value={false} key="disabled">Disabled</MenuItem>,
                    ]}
                  </TextField>
                  <Button
                    variant="contained"
                    disabled={savingKey === control.key}
                    onClick={() => updateSetting(control.key)}
                  >
                    {savingKey === control.key ? "Saving" : "Save"}
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Blueprint coverage</Typography>
          {Object.entries(CLAUDE_EXAM_DOMAINS).map(([key, domain]) => (
            <Box key={key} sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #eee" }}>
              <Typography>{domain.label} ({domain.weight}%)</Typography>
              <Typography fontWeight={700}>
                {bankSummary.domainCounts?.[key] || 0} questions
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClaudePracticeTestAdmin;
